import { useCallback, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Bookmark, Search } from 'lucide-react';
import LocationSearch from 'components/location-search';
import MapFiltersPanel from 'components/search/map/map-filters-panel';
import Thumbnail from 'components/atomic/atoms/thumbnail';
import { SavedSearchThumbnailPlaceholder } from 'ui/saved-search';
import { useUserContext } from 'contexts/user';
import { useModalContext, LOGIN_REGISTRATION_MODAL } from 'contexts/modal';
import type { Filter } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import { SavedSearchPanel, type SelectedSavedSearch } from 'components/pages/area-page-v2/panes/saved-search-panel';
import type { MapBounds } from 'domain/saved-search';
import { useLocationSearchBar } from './use-location-search-bar';
import type { PartialDeep } from 'type-fest';
import styles from './top-bar.module.scss';

export type TopBarProps = {
  areaName?: string;
  /**
   * The map's current rectangle, recorded on a search saved from a page with no
   * area slug to locate it by. Pages that always have a slug can leave it out.
   */
  mapBounds?: MapBounds | null;
  /**
   * True on a page where the map is the search (the search page), so a saved
   * search records the map's rectangle instead of any slug in the filter.
   */
  locatedByMap?: boolean;
  /** Called when the user picks "All Saved Searches" in the saved-search panel. */
  onViewAllSavedSearches?: () => void;
  /** The current applied search. Drives both the filters panel and the saved-search panel. */
  filter: Filter;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onFiltersValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  /** Applies a selected saved search's filters and pans the map to its location. */
  onApplySavedSearch: (filter: Filter) => void;
  filtersResultsCount?: number;
  onSavedSearchesClick: () => void;
  /** Called whenever the filters popover opens or closes. */
  onFiltersOpenChange?: (isOpen: boolean) => void;
  /**
   * Replaces the location input with a trigger that expands the search bar into
   * the smart search panel. The panel itself is rendered by the caller.
   */
  isSmartSearchEnabled?: boolean;
  isSmartSearchOpen?: boolean;
  /** Text shown in the collapsed trigger, i.e. the current smart search query. */
  smartSearchQuery?: string;
  onSmartSearchOpen?: () => void;
  /** Expanded panel, docked to the search field's left edge while open. */
  smartSearchPanel?: ReactNode;
  /** Optional tenant-specific action rendered after saved searches. */
  trailingAction?: ReactNode;
};

export function TopBar({
  areaName,
  filter,
  mapBounds,
  locatedByMap,
  onViewAllSavedSearches,
  onLocationSearchClick,
  onFiltersValueChange,
  onApplySavedSearch,
  filtersResultsCount,
  onSavedSearchesClick,
  onFiltersOpenChange,
  isSmartSearchEnabled = false,
  isSmartSearchOpen = false,
  smartSearchQuery,
  onSmartSearchOpen,
  smartSearchPanel,
  trailingAction,
}: TopBarProps) {
  const { isAuthenticated } = useUserContext();
  const { openModal } = useModalContext();
  const [savedSearchAnchor, setSavedSearchAnchor] = useState<HTMLElement | null>(null);
  const [selectedSearch, setSelectedSearch] = useState<SelectedSavedSearch | null>(null);

  // Changing the location no longer matches the selected saved search — clear it.
  const handleLocationSearchClickWithClear = useCallback((searchPrediction: SearchPrediction) => {
    setSelectedSearch(null);
    onLocationSearchClick(searchPrediction);
  }, [onLocationSearchClick]);

  const {
    locationQuery,
    setLocationQuery,
    searchBarPlaceholder,
    isLocationDropdownActive,
    setIsLocationDropdownActive,
    handleLocationSearchClick,
    handleSearchIconClick,
    searchContainerRef,
  } = useLocationSearchBar({ areaName, onLocationSearchClick: handleLocationSearchClickWithClear });

  const handleFiltersValueChange = useCallback((filters: PartialDeep<Filter>, changedFilter?: string) => {
    // Editing the filters no longer matches the selected saved search — clear it.
    setSelectedSearch(null);
    onFiltersValueChange(filters, changedFilter);
  }, [onFiltersValueChange]);

  const handleSavedSearchesClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // Gate the panel behind auth: a logged-out user gets the login modal instead.
    if (!isAuthenticated) {
      openModal(LOGIN_REGISTRATION_MODAL);
      return;
    }
    const target = event.currentTarget;
    setSavedSearchAnchor(prev => (prev ? null : target));
    onSavedSearchesClick();
  }, [isAuthenticated, openModal, onSavedSearchesClick]);

  const handleSelectSearch = useCallback((search: SelectedSavedSearch, options?: { applyToPage?: boolean }) => {
    setSelectedSearch(search);
    // A search activated right after saving is already the current view, so skip
    // re-applying its filters / re-panning the map.
    if (options?.applyToPage !== false) {
      onApplySavedSearch(search.filter);
    }
  }, [onApplySavedSearch]);

  return (
    <header data-testid="AreaPageV2-TopBar" className={styles.root}>
      <div className={styles.centerCluster}>
        <div
          className={isSmartSearchOpen ? `${styles.search} ${styles.searchExpanded}` : styles.search}
          ref={searchContainerRef}
          data-testid="AreaPageV2-TopBar-Search"
        >
          {!isSmartSearchOpen && (
            <Search className={styles.searchLeadingIcon} strokeWidth={2} aria-hidden />
          )}
          {isSmartSearchEnabled ? (
            // Hidden while expanded: the panel below takes over as the one and
            // only search field, rather than shadowing this one.
            !isSmartSearchOpen && (
              <button
                type="button"
                className={styles.smartSearchTrigger}
                onClick={onSmartSearchOpen}
                aria-expanded={isSmartSearchOpen}
                data-testid="AreaPageV2-TopBar-SmartSearchTrigger"
              >
                {smartSearchQuery || (
                  <span className={styles.smartSearchPlaceholder}>
                    {searchBarPlaceholder
                      ? `Search ${searchBarPlaceholder}`
                      : 'Search'}
                  </span>
                )}
              </button>
            )
          ) : (
            <LocationSearch
              locationQuery={locationQuery}
              setLocationQuery={setLocationQuery}
              isLocationDropdownActive={isLocationDropdownActive}
              setIsLocationDropdownActive={setIsLocationDropdownActive}
              onClick={handleLocationSearchClick}
              placeholder={searchBarPlaceholder || 'Search'}
              onSearchIconClick={handleSearchIconClick}
              className={styles.locationSearch}
            />
          )}
          {smartSearchPanel && (
            <div className={styles.smartSearchDock}>{smartSearchPanel}</div>
          )}
        </div>
        <MapFiltersPanel
          filters={filter}
          onValueChange={handleFiltersValueChange}
          resultsCount={filtersResultsCount}
          className={styles.filtersPanel}
          onOpenChange={onFiltersOpenChange}
        />
        <button
          type="button"
          className={selectedSearch ? styles.savedSearchSelected : styles.iconAction}
          onClick={handleSavedSearchesClick}
          data-testid="AreaPageV2-TopBar-SavedSearches"
          aria-label="Saved searches"
        >
          {selectedSearch ? (
            <>
              <Thumbnail src={selectedSearch.imageUrl} fallback={<SavedSearchThumbnailPlaceholder iconSize={14} />} size={24} radius="6px" sizes="24px" unoptimized />
              <span className={styles.savedSearchSelectedTitle}>{selectedSearch.title}</span>
            </>
          ) : (
            <Bookmark className={styles.actionIcon} strokeWidth={2} aria-hidden />
          )}
        </button>
        {trailingAction && (
          <div className={styles.trailingAction}>{trailingAction}</div>
        )}
      </div>
      <SavedSearchPanel
        open={Boolean(savedSearchAnchor)}
        anchorEl={savedSearchAnchor}
        onClose={() => setSavedSearchAnchor(null)}
        currentFilter={filter}
        mapBounds={mapBounds}
        locatedByMap={locatedByMap}
        onViewAllSavedSearches={onViewAllSavedSearches}
        selectedId={selectedSearch?.id}
        onSelectSearch={handleSelectSearch}
        onUnselectSearch={() => setSelectedSearch(null)}
      />
    </header>
  );
}
